// src/hooks/useFeatureAccess.js

import { useAuth } from '../context/AuthContext';
import {
  PLAN_LIMITS,
  SUBSCRIPTION_STATUS,
  hasFeatureInPlan,
  getRequiredPlanForFeature,
  getPlanDisplayName
} from '../utils/subscriptionPlans';

/**
 * Hook for checking feature access based on subscription plan
 * @returns {Object} Feature access utilities
 */
export function useFeatureAccess() {
  const { subscription } = useAuth();

  /**
   * Check if the current subscription has access to a feature
   * @param {string} featureName - Feature flag from FEATURES
   * @returns {boolean}
   */
  const hasFeature = (featureName) => {
    if (!subscription || !subscription.plan) {
      return false;
    }

    // Always allow access during trial
    if (subscription.status === SUBSCRIPTION_STATUS.TRIALING) {
      const planLimits = PLAN_LIMITS[subscription.plan];
      return planLimits.features.includes(featureName);
    }

    // Check if subscription is active
    if (subscription.status !== SUBSCRIPTION_STATUS.ACTIVE) {
      return false;
    }

    return hasFeatureInPlan(subscription.plan, featureName);
  };

  /**
   * Check if a new team member can be added
   * @param {number} currentSeats - Current number of team members
   * @returns {Object} { allowed, canAddOverage, reason, baseLimit, overageSeats }
   */
  const canAddTeamMember = (currentSeats = 0) => {
    if (!subscription || !subscription.plan) {
      return { allowed: false, canAddOverage: false, reason: 'No active subscription' };
    }

    const planLimits = PLAN_LIMITS[subscription.plan];

    // -1 means unlimited seats
    if (planLimits.seats === -1) {
      return { allowed: true, canAddOverage: false };
    }

    const overageSeats = subscription.overageSeats || 0;
    const totalAllowed = planLimits.seats + overageSeats;

    // Within total allowed (base + overage)
    if (currentSeats < totalAllowed) {
      return { allowed: true, canAddOverage: false };
    }

    // Over limit — can they add overage? Only if they have an active Stripe subscription
    const hasStripeSubscription = !!subscription.stripeSubscriptionId;
    return {
      allowed: false,
      canAddOverage: hasStripeSubscription,
      reason: `Seat limit reached (${totalAllowed} seats)`,
      baseLimit: planLimits.seats,
      overageSeats,
    };
  };

  /**
   * Get the limit for a specific resource type
   * @param {string} limitType - Type of limit (e.g., 'invoicesPerMonth', 'seats')
   * @returns {number} -1 for unlimited, 0 for none, or specific number
   */
  const getLimit = (limitType) => {
    if (!subscription || !subscription.plan) {
      return 0;
    }

    const planLimits = PLAN_LIMITS[subscription.plan];
    return planLimits[limitType] ?? 0;
  };

  /**
   * Check if usage is within the plan limit
   * @param {string} limitType - Type of limit to check
   * @param {number} currentUsage - Current usage count
   * @returns {boolean}
   */
  const isWithinLimit = (limitType, currentUsage) => {
    const limit = getLimit(limitType);

    // -1 means unlimited
    if (limit === -1) {
      return true;
    }

    return currentUsage < limit;
  };

  /**
   * Get remaining usage for a limit type
   * @param {string} limitType - Type of limit
   * @param {number} currentUsage - Current usage
   * @returns {number} Remaining usage (-1 for unlimited)
   */
  const getRemainingUsage = (limitType, currentUsage) => {
    const limit = getLimit(limitType);

    if (limit === -1) {
      return -1; // unlimited
    }

    return Math.max(0, limit - currentUsage);
  };

  /**
   * Check if subscription is active (including trial)
   * @returns {boolean}
   */
  const isActive = () => {
    if (!subscription) return false;

    return (
      subscription.status === SUBSCRIPTION_STATUS.ACTIVE ||
      subscription.status === SUBSCRIPTION_STATUS.TRIALING
    );
  };

  /**
   * Check if subscription is in trial period
   * @returns {boolean}
   */
  const isTrialing = () => {
    return subscription?.status === SUBSCRIPTION_STATUS.TRIALING;
  };

  /**
   * Check if subscription is past due
   * @returns {boolean}
   */
  const isPastDue = () => {
    return subscription?.status === SUBSCRIPTION_STATUS.PAST_DUE;
  };

  /**
   * Get days remaining in trial
   * @returns {number|null} Days remaining or null if not in trial
   */
  const getTrialDaysRemaining = () => {
    if (!isTrialing() || !subscription?.trialEndsAt) {
      return null;
    }

    const now = new Date();
    const trialEnd = subscription.trialEndsAt.toDate
      ? subscription.trialEndsAt.toDate()
      : new Date(subscription.trialEndsAt);

    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  };

  /**
   * Get the minimum plan required for a feature
   * @param {string} featureName - Feature flag
   * @returns {string} Plan name
   */
  const getRequiredPlan = (featureName) => {
    return getRequiredPlanForFeature(featureName);
  };

  /**
   * Get upgrade message for a feature
   * @param {string} featureName - Feature flag
   * @returns {string} Upgrade message
   */
  const getUpgradeMessage = (featureName) => {
    const requiredPlan = getRequiredPlan(featureName);
    const planName = getPlanDisplayName(requiredPlan);

    return `Upgrade to ${planName} plan to access this feature`;
  };

  /**
   * Check if user can upgrade from current plan
   * @param {string} targetPlan - Plan to upgrade to
   * @returns {boolean}
   */
  const canUpgrade = (targetPlan) => {
    if (!subscription || !subscription.plan) {
      return true; // Can always upgrade from no plan
    }

    const planHierarchy = {
      'free': 0,
      'trial': 0,
      'starter': 1,
      'growth': 2,
      'scale': 3
    };

    const currentLevel = planHierarchy[subscription.plan] ?? 0;
    const targetLevel = planHierarchy[targetPlan] ?? 0;

    return targetLevel > currentLevel;
  };

  return {
    // Feature checks
    hasFeature,
    hasAccess: hasFeature, // Alias for convenience
    getRequiredPlan,
    getUpgradeMessage,

    // Limit checks
    canAddTeamMember,
    getLimit,
    isWithinLimit,
    getRemainingUsage,

    // Status checks
    isActive: isActive(),
    isTrialing: isTrialing(),
    isPastDue: isPastDue(),
    getTrialDaysRemaining,

    // Plan info
    plan: subscription?.plan || 'free',
    status: subscription?.status || 'inactive',
    planDisplayName: getPlanDisplayName(subscription?.plan || 'free'),

    // Upgrade utilities
    canUpgrade,

    // Raw subscription data
    subscription
  };
}
